import React, { useState, useEffect } from "react";
import {
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Typography,
  Box,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import toast from "react-hot-toast";
import SettingsIcon from "@mui/icons-material/Settings";
import CloseIcon from "@mui/icons-material/Close";

import {
  setSettingsData,
  setRevenueSpreadData,
  setShowSettingsDialogBox,
  setSettingsDialogBoxTab,
} from "../../../../store/slices/projectSlice";
import { toastStyle } from "../../Containers/continerUtilityFunctions";

import BVTSettingsFields from "./BVTSettingsFieldsComponent";
import DataControlPanel from "./ProjectSettingsDataControlPanelComponent";
import CostStatusSettingsPanel from "./ProjectSettingsCostStatusSettingsComponent";

import { saveProjectSettings, saveRevenueSpread } from "../../Containers/api";
import RevenueSpreadSettingsFields from "./RevenueSpreadSettingsFieldsComponent";
import BudgetSnapshotSelector from "./BudgetSnapshotSelectorComponent";

const useReduxSelectors = () => {
  const settingsData = useSelector((state) => state.project.settingsData);
  const revenueSpreadData = useSelector((state) => state.project.revenueSpreadData);
  const selectedProjectId = useSelector((state) => state.project.selectedProjectId);
  const showSettingsDialogBox = useSelector((state) => state.project.showSettingsDialogBox);
  const settingsDialogBoxTab = useSelector((state) => state.project.settingsDialogBoxTab);
  return { settingsData, revenueSpreadData, selectedProjectId, showSettingsDialogBox, settingsDialogBoxTab };
};

const HEADER_BG = "#093d61";

const ProjectSettingsModal = ({
  projectSnapshots,
  budgetData,
  selectedSnapshots,
  initialSnapshots,
  setProjectData,
  setTeamData,
  setBudgetData,
}) => {
  const dispatch = useDispatch();
  const { settingsData, revenueSpreadData, selectedProjectId, showSettingsDialogBox, settingsDialogBoxTab } =
    useReduxSelectors();

  const [bvtFields, setBvtFields] = useState({
    contract_structure: "",
    job_has_deferred_tax: false,
    neg_insurance_percent: "",
    neg_fee_percent: "",
    shared_savings: false,
    shared_savings_split_percent_to_arco: "",
    max_shared_savings_amt: "",
    phd_sync: false,
  });
  const [revSpreadFields, setRevSpreadFields] = useState({
    sfdc_total_revenue: "",
    sfdc_off_the_books_revenue: "",
    unearned_revenue: "",
    ra: [],
  });
  const [percentTotalError, setPercentTotalError] = useState(false);
  const [monthlyCostStatusActive, setMonthlyCostStatusActive] = useState(false);
  const [recipients, setRecipients] = useState();

  const handleTabChange = (_e, newValue) => dispatch(setSettingsDialogBoxTab(newValue));

  useEffect(() => {
    if (settingsData?.other_meta_data) setBvtFields(settingsData.other_meta_data);
    setRecipients(settingsData?.monthly_cost_status_data?.cost_status_email_recipients ?? []);
    setMonthlyCostStatusActive(settingsData?.monthly_cost_status_data?.monthly_cost_status_active ?? false);
  }, [settingsData]);

  useEffect(() => {
    if (revenueSpreadData) setRevSpreadFields(revenueSpreadData);
  }, [revenueSpreadData]);

  const handleSave = () => {
    const sanitizedBvtFields = {
      ...bvtFields,
      job_has_deferred_tax: !!bvtFields.job_has_deferred_tax,
      shared_savings: !!bvtFields.shared_savings,
    };
    const requestBody = {
      project_id: selectedProjectId,
      other_meta_data: sanitizedBvtFields,
      monthly_cost_status_data: {
        monthly_cost_status_active: monthlyCostStatusActive,
        cost_status_email_recipients: recipients?.map((r) => r.email) ?? [],
      },
    };

    (async () => {
      try {
        const resData = await saveProjectSettings(requestBody);
        if (resData.success) {
          const revSpreadResData = await saveRevenueSpread({
            project_id: selectedProjectId,
            ...revSpreadFields,
          });
          if (revSpreadResData.error) {
            toast.error(`Error: ${revSpreadResData.error}`, toastStyle());
          } else if (revSpreadResData.success) {
            dispatch(setSettingsData(requestBody));
            toast.success(
              "Project Settings Saved Successfully. It takes about 2 minutes to refresh the calculations.",
              toastStyle()
            );
          }
        } else {
          console.error("Error updating snapshots and settings:", resData.message);
        }
      } catch (error) {
        console.error("Error making POST request:", error);
        toast.error(error.message || "An unexpected error occurred.", toastStyle());
      }
    })();

    dispatch(setShowSettingsDialogBox(false));
  };

  return (
    <>
      {/* Triggers */}
      <Typography
        variant="subtitle1"
        className="titillium-web project-header-title-text"
        sx={{ color: "white", textAlign: "left", textDecoration: "underline", cursor: "pointer", fontSize: "1rem" }}
        onClick={() => dispatch(setShowSettingsDialogBox(true))}
      >
        Project Settings
      </Typography>

      <IconButton onClick={() => dispatch(setShowSettingsDialogBox(true))} sx={{ textAlign: "right" }} size="large">
        <SettingsIcon sx={{ color: "white", ml: -0.5, mr: -1 }} />
      </IconButton>

      {/* Modal */}
      <Dialog
        open={showSettingsDialogBox}
        onClose={() => dispatch(setShowSettingsDialogBox(false))}
        maxWidth={false}
        sx={{
          "& .MuiDialog-paper": {
            height: "80vh",
            maxHeight: "90vh",
            width: "95vw",
            maxWidth: 1600,
            backgroundColor: "background.paper",
            color: "text.primary",
            borderRadius: 2,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Title bar - dark */}
        <DialogTitle
          className="titillium-web-base"
          sx={{
            fontSize: 30,
            fontWeight: "bolder",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: HEADER_BG,
            color: "#fff",
            py: 1.5,
            pr: 1,
          }}
        >
          Project Settings
          <IconButton onClick={() => dispatch(setShowSettingsDialogBox(false))} sx={{ color: "#fff" }} size="large">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {/* Tabs - default colors so indicator is visible */}
        <Box sx={{ display: "flex", justifyContent: "center", px: { xs: 2, md: 3 }, pt: 2 }}>
          <Tabs
            value={settingsDialogBoxTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab label="BUDGET VARIANCE SETTINGS" />
            <Tab label="REVENUE SPREAD DATA" />
            <Tab label="SNAPSHOTS LIST" />
            <Tab label="DATA CONTROL PANEL" />
            <Tab label="COST STATUS SETTINGS" />
          </Tabs>
        </Box>

        {/* Body */}
        <DialogContent
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
            px: { xs: 2, md: 5 },
            py: 3,
            minHeight: 0,
          }}
        >
          {settingsDialogBoxTab === 0 && <BVTSettingsFields bvtFields={bvtFields} setBvtFields={setBvtFields} />}

          {settingsDialogBoxTab === 1 && (
            <RevenueSpreadSettingsFields
              revSpreadFields={revSpreadFields}
              setRevSpreadFields={setRevSpreadFields}
              percentTotalError={percentTotalError}
              setPercentTotalError={setPercentTotalError}
            />
          )}

          {settingsDialogBoxTab === 2 && <BudgetSnapshotSelector initialSnapshots={initialSnapshots} />}

          {settingsDialogBoxTab === 3 && (
            <DataControlPanel
              setTeamData={setTeamData}
              setBudgetData={setBudgetData}
              setProjectData={setProjectData}
              setSettingsData={setSettingsData}
            />
          )}

          {settingsDialogBoxTab === 4 && (
            <CostStatusSettingsPanel
              monthlyCostStatusActive={monthlyCostStatusActive}
              setMonthlyCostStatusActive={setMonthlyCostStatusActive}
              recipients={recipients}
              setRecipients={setRecipients}
              bvtFields={bvtFields}
              setBvtFields={setBvtFields}
            />
          )}
        </DialogContent>

        {/* Footer */}
        <DialogActions 
          sx={{ 
            p: 2, 
            gap: 1, 
            justifyContent: "flex-end",
            flexShrink: 0,
            borderTop: 1,
            borderColor: "divider",
            backgroundColor: "background.paper",
            minHeight: 34, 
          }}
        >
          <Button onClick={() => dispatch(setShowSettingsDialogBox(false))} variant="outlined">
            Close
          </Button>
          <Button onClick={handleSave} disabled={percentTotalError} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProjectSettingsModal;
